/**
 * 国优炬康 状态机枚举(Week 2/3 末冻结)
 *
 * 13 个核心状态机:
 * 1. 用户登录状态
 * 2. 订单状态
 * 3. 售后状态
 * 4. 退款状态
 * 5. 分销佣金状态
 * 6. 提现状态
 * 7. 内容状态
 * 8. 咨询状态
 * 9. 广场状态
 * 10. 佣金冲正状态
 * 11. 财务对账状态
 * 12. 健康导出状态
 * 13. 设备联调状态
 *
 * 详见:knowledge-base/08-对外合作/国优炬康/03-技术方案/研发交付方案/19-l4-code-implementation.md
 */

// ───── 1. 用户登录状态 ─────
export enum LoginStatus {
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  WECHAT_PENDING = 'WECHAT_PENDING', // 微信授权完成,等待手机号授权
  PHONE_AUTHORIZED = 'PHONE_AUTHORIZED', // 手机号已授权,完成登录
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  BANNED = 'BANNED',
}

export const LoginStatusName: Record<LoginStatus, string> = {
  [LoginStatus.UNAUTHENTICATED]: '未登录',
  [LoginStatus.WECHAT_PENDING]: '待手机号授权',
  [LoginStatus.PHONE_AUTHORIZED]: '已登录',
  [LoginStatus.TOKEN_EXPIRED]: 'Token 已过期',
  [LoginStatus.BANNED]: '已封禁',
};

// ───── 2. 订单状态 ─────
export enum OrderStatus {
  PENDING_PAYMENT = 'PENDING_PAYMENT', // 未支付
  PAID = 'PAID', // 待发货
  SHIPPED = 'SHIPPED', // 已发货
  COMPLETED = 'COMPLETED', // 已完成
  CANCELLED = 'CANCELLED', // 已关闭
  REFUNDING = 'REFUNDING', // 退款中(历史状态,已废弃)
  PARTIAL_REFUNDED = 'PARTIAL_REFUNDED', // 部分退款(历史状态,已废弃)
  FULL_REFUNDED = 'FULL_REFUNDED', // 已退款
}

/** 当前有效的订单主状态(6个) */
export const ACTIVE_ORDER_STATUSES = [
  OrderStatus.PENDING_PAYMENT,
  OrderStatus.PAID,
  OrderStatus.SHIPPED,
  OrderStatus.COMPLETED,
  OrderStatus.CANCELLED,
  OrderStatus.FULL_REFUNDED,
] as const;

export const OrderStatusName: Record<OrderStatus, string> = {
  [OrderStatus.PENDING_PAYMENT]: '未支付',
  [OrderStatus.PAID]: '待发货',
  [OrderStatus.SHIPPED]: '已发货',
  [OrderStatus.COMPLETED]: '已完成',
  [OrderStatus.CANCELLED]: '已关闭',
  [OrderStatus.REFUNDING]: '退款中',
  [OrderStatus.PARTIAL_REFUNDED]: '部分退款',
  [OrderStatus.FULL_REFUNDED]: '已退款',
};

// ───── 2a. 发货方式 ─────
export enum ShipMethod {
  EXPRESS = 'EXPRESS', // 物流快递
  LOCAL = 'LOCAL', // 同城配送
  PICKUP = 'PICKUP', // 用户自提
  VIRTUAL = 'VIRTUAL', // 虚拟发货
}

export const ShipMethodName: Record<ShipMethod, string> = {
  [ShipMethod.EXPRESS]: '物流快递',
  [ShipMethod.LOCAL]: '同城配送',
  [ShipMethod.PICKUP]: '用户自提',
  [ShipMethod.VIRTUAL]: '虚拟发货',
};

// ───── 3. 售后状态 ─────
export enum AfterSaleStatus {
  NONE = 'NONE',
  REQUESTED = 'REQUESTED', // 售后申请中
  REVIEWING = 'REVIEWING', // 后台审核中
  APPROVED = 'APPROVED', // 已通过
  REJECTED = 'REJECTED', // 已拒绝
  COMPLETED = 'COMPLETED', // 已完成
}

export const AfterSaleStatusName: Record<AfterSaleStatus, string> = {
  [AfterSaleStatus.NONE]: '无售后',
  [AfterSaleStatus.REQUESTED]: '申请中',
  [AfterSaleStatus.REVIEWING]: '审核中',
  [AfterSaleStatus.APPROVED]: '已通过',
  [AfterSaleStatus.REJECTED]: '已拒绝',
  [AfterSaleStatus.COMPLETED]: '已完成',
};

// ───── 4. 退款状态 ─────
export enum RefundStatus {
  PENDING = 'PENDING', // 退款待发起
  PROCESSING = 'PROCESSING', // 退款处理中(已调微信)
  SUCCESS = 'SUCCESS', // 退款成功
  FAILED = 'FAILED', // 退款失败
  REJECTED = 'REJECTED', // 审核拒绝
  CLOSED = 'CLOSED', // 退款关闭(订单关闭)
}

export const RefundStatusName: Record<RefundStatus, string> = {
  [RefundStatus.PENDING]: '待发起',
  [RefundStatus.PROCESSING]: '处理中',
  [RefundStatus.SUCCESS]: '退款成功',
  [RefundStatus.FAILED]: '退款失败',
  [RefundStatus.REJECTED]: '审核拒绝',
  [RefundStatus.CLOSED]: '退款关闭',
};

// ───── 5. 分销佣金状态 ─────
export enum CommissionStatus {
  PENDING = 'PENDING', // 待结算
  FROZEN = 'FROZEN', // 冻结期
  AVAILABLE = 'AVAILABLE', // 可提现
  WITHDRAWN = 'WITHDRAWN', // 已提现
  REVERSED = 'REVERSED', // 已冲正(退款触发)
}

export const CommissionStatusName: Record<CommissionStatus, string> = {
  [CommissionStatus.PENDING]: '待结算',
  [CommissionStatus.FROZEN]: '冻结期',
  [CommissionStatus.AVAILABLE]: '可提现',
  [CommissionStatus.WITHDRAWN]: '已提现',
  [CommissionStatus.REVERSED]: '已冲正',
};

// ───── 6. 提现状态 ─────
export enum WithdrawalStatus {
  REQUESTED = 'REQUESTED', // 申请中
  REVIEWING = 'REVIEWING', // 后台审核中
  APPROVED = 'APPROVED', // 已通过
  REJECTED = 'REJECTED', // 已拒绝
  PROCESSING = 'PROCESSING', // 打款中
  SUCCESS = 'SUCCESS', // 打款成功
  FAILED = 'FAILED', // 打款失败
}

export const WithdrawalStatusName: Record<WithdrawalStatus, string> = {
  [WithdrawalStatus.REQUESTED]: '申请中',
  [WithdrawalStatus.REVIEWING]: '审核中',
  [WithdrawalStatus.APPROVED]: '已通过',
  [WithdrawalStatus.REJECTED]: '已拒绝',
  [WithdrawalStatus.PROCESSING]: '打款中',
  [WithdrawalStatus.SUCCESS]: '打款成功',
  [WithdrawalStatus.FAILED]: '打款失败',
};

// ───── 7. 内容状态 ─────
export enum ContentStatus {
  DRAFT = 'DRAFT', // 草稿
  PENDING_REVIEW = 'PENDING_REVIEW', // 待审核
  PUBLISHED = 'PUBLISHED', // 已发布
  REJECTED = 'REJECTED', // 已拒绝
  OFFLINE = 'OFFLINE', // 已下架
}

export const ContentStatusName: Record<ContentStatus, string> = {
  [ContentStatus.DRAFT]: '草稿',
  [ContentStatus.PENDING_REVIEW]: '待审核',
  [ContentStatus.PUBLISHED]: '已发布',
  [ContentStatus.REJECTED]: '已拒绝',
  [ContentStatus.OFFLINE]: '已下架',
};

// ───── 8. 咨询状态 ─────
export enum ConsultationStatus {
  PENDING = 'PENDING', // 待回复
  REPLIED = 'REPLIED', // 已回复
  CLOSED = 'CLOSED', // 已关闭
  RATED = 'RATED', // 已评价
}

export const ConsultationStatusName: Record<ConsultationStatus, string> = {
  [ConsultationStatus.PENDING]: '待回复',
  [ConsultationStatus.REPLIED]: '已回复',
  [ConsultationStatus.CLOSED]: '已关闭',
  [ConsultationStatus.RATED]: '已评价',
};

// ───── 9. 广场状态 ─────
export enum SquarePostStatus {
  PENDING_REVIEW = 'PENDING_REVIEW',
  PUBLISHED = 'PUBLISHED',
  OFFLINE = 'OFFLINE',
  DELETED = 'DELETED',
}

export const SquarePostStatusName: Record<SquarePostStatus, string> = {
  [SquarePostStatus.PENDING_REVIEW]: '待审核',
  [SquarePostStatus.PUBLISHED]: '已发布',
  [SquarePostStatus.OFFLINE]: '已下架',
  [SquarePostStatus.DELETED]: '已删除',
};

// ───── 9.5 视频评论审核状态 ─────
export enum VideoCommentStatus {
  PENDING = 'PENDING', // 待审核
  PUBLISHED = 'PUBLISHED', // 已发布(审核通过)
  HIDDEN = 'HIDDEN', // 已隐藏(驳回/下架)
}

export const VideoCommentStatusName: Record<VideoCommentStatus, string> = {
  [VideoCommentStatus.PENDING]: '待审核',
  [VideoCommentStatus.PUBLISHED]: '已发布',
  [VideoCommentStatus.HIDDEN]: '已隐藏',
};

// ───── 10. 佣金冲正状态 ─────
export enum CommissionReverseStatus {
  TRIGGERED = 'TRIGGERED', // 触发(订单退款)
  PROCESSING = 'PROCESSING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

export const CommissionReverseStatusName: Record<CommissionReverseStatus, string> = {
  [CommissionReverseStatus.TRIGGERED]: '已触发',
  [CommissionReverseStatus.PROCESSING]: '处理中',
  [CommissionReverseStatus.SUCCESS]: '冲正成功',
  [CommissionReverseStatus.FAILED]: '冲正失败',
};

// ───── 12. 健康导出状态 ─────
export enum HealthExportStatus {
  REQUESTED = 'REQUESTED',
  PROCESSING = 'PROCESSING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

export const HealthExportStatusName: Record<HealthExportStatus, string> = {
  [HealthExportStatus.REQUESTED]: '已请求',
  [HealthExportStatus.PROCESSING]: '生成中',
  [HealthExportStatus.SUCCESS]: '已生成',
  [HealthExportStatus.FAILED]: '生成失败',
};

// ───── 13. 设备联调状态 ─────
export enum DeviceSyncStatus {
  NOT_BOUND = 'NOT_BOUND', // 未绑定
  BOUND = 'BOUND', // 已绑定
  SYNCING = 'SYNCING', // 同步中
  SUCCESS = 'SUCCESS', // 同步成功
  FAILED = 'FAILED', // 同步失败(失败兜底)
  MANUAL_ENTRY = 'MANUAL_ENTRY', // 手动录入
}

export const DeviceSyncStatusName: Record<DeviceSyncStatus, string> = {
  [DeviceSyncStatus.NOT_BOUND]: '未绑定',
  [DeviceSyncStatus.BOUND]: '已绑定',
  [DeviceSyncStatus.SYNCING]: '同步中',
  [DeviceSyncStatus.SUCCESS]: '同步成功',
  [DeviceSyncStatus.FAILED]: '同步失败',
  [DeviceSyncStatus.MANUAL_ENTRY]: '手动录入',
};

// ───── 通用业务枚举 ─────

/** 后台角色 */
export enum AdminRole {
  ADMIN = 'ADMIN', // 超级管理员
  OPS = 'OPS', // 运营
  GOODS = 'GOODS', // 商品
  ORDER = 'ORDER', // 订单
  FIN = 'FIN', // 财务
  CONTENT = 'CONTENT', // 内容
  HEALTH = 'HEALTH', // 健康
  DOCTOR = 'DOCTOR', // 医生(绑定会员/查看健康档案/答复咨询,语义同健康管理师)
  VOL = 'VOL', // 志愿者
  AUDIT = 'AUDIT', // 审计
}

export const AdminRoleName: Record<AdminRole, string> = {
  [AdminRole.ADMIN]: '超级管理员',
  [AdminRole.OPS]: '运营',
  [AdminRole.GOODS]: '商品',
  [AdminRole.ORDER]: '订单',
  [AdminRole.FIN]: '财务',
  [AdminRole.CONTENT]: '内容',
  [AdminRole.HEALTH]: '健康',
  [AdminRole.DOCTOR]: '医生',
  [AdminRole.VOL]: '志愿者',
  [AdminRole.AUDIT]: '审计',
};

/** 第三方集成类型 */
export enum ThirdPartyType {
  OSS = 'OSS',
  LOGISTICS = 'LOGISTICS',
  DIET_AI = 'DIET_AI',
  LIVE = 'LIVE',
  DEVICE = 'DEVICE',
}

/** 第三方集成配置状态 */
export enum ThirdPartyConfigStatus {
  NOT_CONFIGURED = 'NOT_CONFIGURED',
  CONFIGURED = 'CONFIGURED',
  DISABLED = 'DISABLED',
}

/** 提现方式 */
export enum WithdrawalMethod {
  OFFLINE = 'OFFLINE', // 线下打款
  AUTO = 'AUTO', // 自动打款
}

/** 支付方式 */
export enum PaymentMethod {
  WECHAT = 'WECHAT', // 微信支付
  BALANCE = 'BALANCE', // 余额支付(预留)
}

/** 退款方式 */
export enum RefundMethod {
  ORIGINAL = 'ORIGINAL', // 原路退回
  BALANCE = 'BALANCE', // 退回余额(预留)
}

/** 支付状态 */
export enum PaymentStatus {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED',
}

export const PaymentStatusName: Record<PaymentStatus, string> = {
  [PaymentStatus.PENDING]: '待支付',
  [PaymentStatus.SUCCESS]: '支付成功',
  [PaymentStatus.FAILED]: '支付失败',
  [PaymentStatus.CANCELLED]: '已取消',
  [PaymentStatus.REFUNDED]: '已退款',
};

/** 资金流水类型 */
export enum CapitalFlowType {
  PAYMENT = 'PAYMENT',
  REFUND = 'REFUND',
  COMMISSION = 'COMMISSION',
  WITHDRAWAL = 'WITHDRAWAL',
}

export const CapitalFlowTypeName: Record<CapitalFlowType, string> = {
  [CapitalFlowType.PAYMENT]: '支付',
  [CapitalFlowType.REFUND]: '退款',
  [CapitalFlowType.COMMISSION]: '佣金',
  [CapitalFlowType.WITHDRAWAL]: '提现',
};

/** 资金流水方向 */
export enum CapitalFlowDirection {
  IN = 'IN',
  OUT = 'OUT',
}

export const CapitalFlowDirectionName: Record<CapitalFlowDirection, string> = {
  [CapitalFlowDirection.IN]: '收入',
  [CapitalFlowDirection.OUT]: '支出',
};

/** 商品状态 */
export enum ProductStatus {
  DRAFT = 'DRAFT',
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
  OUT_OF_STOCK = 'OUT_OF_STOCK',
}

export const ProductStatusName: Record<ProductStatus, string> = {
  [ProductStatus.DRAFT]: '草稿',
  [ProductStatus.ONLINE]: '已上架',
  [ProductStatus.OFFLINE]: '已下架',
  [ProductStatus.OUT_OF_STOCK]: '缺货',
};

// ───── 健康域业务枚举(饮食/运动/预警/设备对接,健康四模块 M0 新增) ─────

/** 餐次 */
export enum MealType {
  BREAKFAST = 'BREAKFAST', // 早餐
  LUNCH = 'LUNCH', // 午餐
  DINNER = 'DINNER', // 晚餐
  SNACK = 'SNACK', // 加餐
}

export const MealTypeName: Record<MealType, string> = {
  [MealType.BREAKFAST]: '早餐',
  [MealType.LUNCH]: '午餐',
  [MealType.DINNER]: '晚餐',
  [MealType.SNACK]: '加餐',
};

/** 运动类型 */
export enum SportType {
  WALKING = 'WALKING',
  RUNNING = 'RUNNING',
  CYCLING = 'CYCLING',
  SWIMMING = 'SWIMMING',
  FITNESS = 'FITNESS',
  BALL = 'BALL',
  OTHER = 'OTHER',
}

export const SportTypeName: Record<SportType, string> = {
  [SportType.WALKING]: '步行',
  [SportType.RUNNING]: '跑步',
  [SportType.CYCLING]: '骑行',
  [SportType.SWIMMING]: '游泳',
  [SportType.FITNESS]: '健身',
  [SportType.BALL]: '球类',
  [SportType.OTHER]: '其他',
};

/** 饮食记录来源 */
export enum DietRecordSource {
  MANUAL = 'MANUAL', // 手动录入
  AI = 'AI', // AI 识别
  RECIPE = 'RECIPE', // 食谱导入
  PHOTO = 'PHOTO', // 拍照打卡(094:仅照片,不识别,0 热量)
}

export const DietRecordSourceName: Record<DietRecordSource, string> = {
  [DietRecordSource.MANUAL]: '手动录入',
  [DietRecordSource.AI]: 'AI 识别',
  [DietRecordSource.RECIPE]: '食谱',
  [DietRecordSource.PHOTO]: '拍照打卡',
};

/** 健康预警类型 */
export enum HealthAlertType {
  BLOOD_SUGAR_HIGH = 'BLOOD_SUGAR_HIGH', // 血糖偏高
  BLOOD_SUGAR_LOW = 'BLOOD_SUGAR_LOW', // 血糖偏低
  MEAL_GAP_LARGE = 'MEAL_GAP_LARGE', // 餐前餐后差值过大
  BLOOD_PRESSURE_HIGH = 'BLOOD_PRESSURE_HIGH', // 血压偏高
  URIC_ACID_HIGH = 'URIC_ACID_HIGH', // 尿酸偏高
}

export const HealthAlertTypeName: Record<HealthAlertType, string> = {
  [HealthAlertType.BLOOD_SUGAR_HIGH]: '血糖偏高',
  [HealthAlertType.BLOOD_SUGAR_LOW]: '血糖偏低',
  [HealthAlertType.MEAL_GAP_LARGE]: '餐前餐后差值过大',
  [HealthAlertType.BLOOD_PRESSURE_HIGH]: '血压偏高',
  [HealthAlertType.URIC_ACID_HIGH]: '尿酸偏高',
};

/** 健康预警状态 */
export enum HealthAlertStatus {
  UNREAD = 'UNREAD',
  READ = 'READ',
  HANDLED = 'HANDLED',
}

export const HealthAlertStatusName: Record<HealthAlertStatus, string> = {
  [HealthAlertStatus.UNREAD]: '未读',
  [HealthAlertStatus.READ]: '已读',
  [HealthAlertStatus.HANDLED]: '已处理',
};

/** 设备厂商 */
export enum DeviceProvider {
  VIVACHEK = 'VIVACHEK', // 微策(NB-IoT 四合一:血糖/血酮/尿酸)
}

/** 微策推送检测项目(sample 字段,协议 v1.5) */
export enum VivachekSample {
  BLOOD_SUGAR = 1, // 血糖
  BLOOD_SUGAR_ALT = 2, // 血糖(备用试纸)
  BLOOD_KETONE = 3, // 血酮
  URIC_ACID = 4, // 尿酸
  UNKNOWN_STRIP = 5, // 未知试纸条
}

/** 设备推送处理状态 */
export enum DevicePushStatus {
  RECORDED = 'RECORDED', // 已入库
  DUPLICATE = 'DUPLICATE', // 重复推送(幂等命中)
  IGNORED_QC = 'IGNORED_QC', // 质控液数据,不入健康记录(历史状态,2026-08-18 起质控改 RECORDED_QC 入库)
  RECORDED_QC = 'RECORDED_QC', // 质控液数据,已入健康记录(data.isQc=true,小程序可见并标记质控)
  UNMATCHED_SN = 'UNMATCHED_SN', // SN 未绑定会员
  UNKNOWN_SAMPLE = 'UNKNOWN_SAMPLE', // 未知检测项目
  REJECTED = 'REJECTED', // 验签失败,拒绝处理
}

export const DevicePushStatusName: Record<DevicePushStatus, string> = {
  [DevicePushStatus.RECORDED]: '已入库',
  [DevicePushStatus.DUPLICATE]: '重复推送',
  [DevicePushStatus.IGNORED_QC]: '质控液忽略',
  [DevicePushStatus.RECORDED_QC]: '质控已入库',
  [DevicePushStatus.UNMATCHED_SN]: 'SN 未绑定',
  [DevicePushStatus.UNKNOWN_SAMPLE]: '未知项目',
  [DevicePushStatus.REJECTED]: '验签失败',
};

/** 订阅消息配额场景 */
export enum SubscribeScene {
  MEDICATION = 'MEDICATION',
  DIET = 'DIET',
  EXERCISE = 'EXERCISE',
  ALERT = 'ALERT',
  ORDER = 'ORDER',
  CONSULT = 'CONSULT',
  VOLUNTEER = 'VOLUNTEER',
}
